import { useContext, useMemo, useState } from 'react';

// Third-parties.
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import ConditionalRender from "../../../helpers/ConditionalRender";

// Helper.
import Helper from '../../../helpers/Helper';
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";

// Types.
import HostTarget from '../../../types/host/HostTarget';
import ChartSeries from '../../../types/ChartSeries';
import ChartPlotLine from '../../../types/ChartPlotLine';
import HostStatisticV2 from '../../../types/host/HostStatisticV2';

// Constants.
import {
    DATA_INITIALISING,
    DATA_LOADING,
    DATA_LOADED,
    CHART_COLOURS_STATEMENTS, CHART_DATA_TYPE
} from '../../Constants';
import { VIEW_MODE } from '../../Constants';
import { getConvertedTimeToUTC, highchartsCredits } from "../../../helpers/utils";
import {useLicences} from "../../../context/LicenceContext";
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import HostApi from "../../../api/host/HostApi";

function DiskChart(props: {host: HostTarget, plotLines?: ChartPlotLine[] }) {
    const [loadingBasic, setLoadingBasic] = useState<number>(DATA_LOADING);
    const [loadingDetail, setLoadingDetail] = useState<number>(DATA_INITIALISING);

    const [writeSeries, setWriteSeries] = useState<ChartSeries[]>([]);
    const [readWrittenSeries, setReadWrittenSeries] = useState<ChartSeries[]>([]);

    const [basicTimeSeries, setBasicTimeSeries] = useState<number[][]>([]);
    const [timeSeries, setTimeSeries] = useState<ChartSeries[]>([]);
    // Cache the v2 response so the breakdown can be built without further round-trips.
    const [v2Response, setV2Response] = useState<HostStatisticV2[]>([]);
    const [mode, setMode] = useState<number>(VIEW_MODE.BASIC);
    const { licences } = useLicences ();
    const timeRangeContext = useContext(TimeRangeContext)

    const timePeak = useMemo(() => {
        return Helper.getPeakValue(basicTimeSeries.map(i => i[1]))
    }, [basicTimeSeries])

    void useMemo(async() => {
        const limit = process.env.REACT_APP_API_LIMIT ? Number(process.env.REACT_APP_API_LIMIT) : undefined;
        const data = await HostApi.getHostStatisticV2Time(buildApiQuery(timeRangeContext.timeRange), [props.host.id], limit);
        setV2Response(data);
        setBasicTimeSeries(data.map(item => {
            return [getConvertedTimeToUTC(item), Number(item.ioutilisation) || 0]
        }));
        setLoadingBasic(DATA_LOADED);
    }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.host]);

    const basicChart = useMemo(() => {
        const basicChartOptions = chartOptionsGranularity([
            {
                name: 'Average disk I/O',
                type: 'spline',
                minHeight: 3,
                data: basicTimeSeries,
                zIndex: 1,
                yAxis: 1,
            }], timePeak, {
            credits: {enabled: highchartsCredits(licences)},
            timeRangeContext: timeRangeContext,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.PERCENTAGE);
                }
            },
            plotLines: props.plotLines || []
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={basicChartOptions}/>;

    }, [basicTimeSeries, timePeak, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, licences, props.plotLines]);


    function buildDetail(data: HostStatisticV2[]) {

        // K read / K written
        const writeMetrics: string[] = ['K read', 'K written'];
        const writeData: any[] = writeMetrics.map((metric, index) => ({
            name: metric,
            color: CHART_COLOURS_STATEMENTS[index],
            type: 'column',
            data: data.map(item => [
                getConvertedTimeToUTC(item),
                Number(item[metric]) || 0,
            ]),
            zIndex: 1,
        }));
        setWriteSeries(writeData);

        // reads completed / writes completed / I/Os currently in progress
        const writtenMetrics: string[] = ['reads completed', 'writes completed', 'I/Os currently in progress'];
        const readWrittenData: any[] = writtenMetrics.map((metric, index) => ({
            name: metric,
            color: CHART_COLOURS_STATEMENTS[index],
            type: 'column',
            data: data.map(item => [
                getConvertedTimeToUTC(item),
                Number(item[metric]) || 0,
            ]),
            zIndex: 1,
        }));
        setReadWrittenSeries(readWrittenData);

        // time-spent metrics — splines for the totals, columns for the per-IO breakdowns
        const timeMetrics: string[] = ['time spent reading (ms)', 'time spent writing (ms)', 'time spent doing I/Os (ms)', 'weighted time spent doing I/Os (ms)'];
        const timeData: any[] = timeMetrics.map((metric, index) => {
            const isSpline = metric === 'time spent doing I/Os (ms)' || metric === 'weighted time spent doing I/Os (ms)';
            return {
                name: metric,
                color: CHART_COLOURS_STATEMENTS[index],
                type: isSpline ? 'spline' : 'column',
                data: data.map(item => [
                    getConvertedTimeToUTC(item),
                    Number(item[metric]) || 0,
                ]),
                zIndex: 1,
                yAxis: isSpline ? 1 : 0,
            };
        });
        setTimeSeries(timeData);

        setLoadingDetail(DATA_LOADED);
    }

    const chartOptions = {
        credits: {enabled: highchartsCredits(licences)},
        timeRangeContext: timeRangeContext,
        legend: {
            align: 'left',
            backgroundColor: 'transparent',
            enabled: false,
            floating: false,
            itemMarginTop: 0,
            itemMarginBottom: 2,
            itemStyle: {
                fontSize: '.7rem'
            },
            layout: 'horizontal',
            padding: 0,
            x: 0,
            y: 0,
            verticalAlign: 'top'
        },
        tooltip: {
            formatter: function () {
                return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.GENERIC);
            }
        },
        plotLines: props.plotLines || []
    }

    const readWritesChart = useMemo(() => {
        const detailChartOptions = chartOptionsGranularity(
            writeSeries, 100, chartOptions)
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={detailChartOptions}/>;

    }, [writeSeries, timePeak, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, licences, props.plotLines]);

    const readWrittenChart = useMemo(() => {
        const detailChartOptions = chartOptionsGranularity(
            readWrittenSeries, 100, chartOptions)
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={detailChartOptions}/>;

    }, [readWrittenSeries, timePeak, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, licences, props.plotLines]);

    const timeChart = useMemo(() => {
        const detailChartOptions = chartOptionsGranularity(
            timeSeries, 100, chartOptions)
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts} options={detailChartOptions}/>;

    }, [timeSeries, timePeak, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, licences, props.plotLines]);

    function toggleChart() {

        // Build the breakdown charts from the cached v2 response — no extra fetch required.
        if (loadingDetail === DATA_INITIALISING) {
            setLoadingDetail(DATA_LOADING)
            buildDetail(v2Response);
        }

        // Toggle the displayed chart.
        setMode(((mode === VIEW_MODE.BASIC) ? VIEW_MODE.DETAIL : VIEW_MODE.BASIC));
    }

    return (
        <div className="card collapsible">
            <div className="card-header">
                <i className="fal fa-chart-bar fa-fw" aria-hidden="true" />
                Average I/O over time
                <i className="collapse-toggle" role="button" data-bs-toggle="collapse" data-bs-target="#collapseDiskCharts" aria-expanded="false" aria-controls="collapseDiskCharts" />
                <div className="btn-group float-end">
                    <button type="button" className="btn btn-xsm btn-primary float-end ms-1" onClick={toggleChart}>{((mode === VIEW_MODE.BASIC) ? 'Show Breakdown' : 'Show Summary')}</button>
                </div>
            </div>
            <div id="collapseDiskCharts" className="card-body collapse show p-0">
                <div className="row row-cols-1">
                    <div className="col p-3">
                        <h4 className='icons-chart-title'>
                            Average I/O
                            {timePeak !== null && (
                                <span className="peak">Peak Average I/O {Math.round(timePeak)}%</span>
                            )}
                        </h4>
                        <ConditionalRender if={mode === VIEW_MODE.BASIC}>
                            <ConditionalRender if={loadingBasic === DATA_LOADING}>
                                <div className="w-100 text-center text-muted mt-3">
                                    <div className="loader spinner chartSpinner">
                                    </div>
                                    <p className="mt-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={loadingBasic === DATA_LOADED}>
                                {basicChart}
                            </ConditionalRender>
                        </ConditionalRender>

                        <ConditionalRender if={mode === VIEW_MODE.DETAIL}>
                            <ConditionalRender if={loadingDetail === DATA_LOADING}>
                                <div className="w-100 text-center text-muted mt-3">
                                    <div className="loader spinner chartSpinner">
                                    </div>
                                    <p className="mt-3">
                                        Loading data...
                                    </p>
                                </div>
                            </ConditionalRender>
                            <ConditionalRender if={loadingDetail === DATA_LOADED}>
                                {readWritesChart}
                                <br />
                                <br />
                                {readWrittenChart}
                                <br />
                                <br />
                                {timeChart}
                            </ConditionalRender>
                        </ConditionalRender>

                    </div>
                </div>
            </div>
        </div>
    );
}

export default DiskChart;
